/**
 * Environment detection utilities
 */

export type Environment = 'local' | 'development' | 'production';

/**
 * Determines the current environment based on hostname
 * @returns 'local' for localhost/127.0.0.1, 'development' for dev.app.headstart.health, 'production' for all others
 */
export function getEnvironment(): Environment {
  const hostname = window.location.hostname;
  
  // Local development (localhost)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  // Dev environment
  if (hostname === 'dev.app.headstart.health') {
    return 'development';
  }
  
  // Production (app.headstart.health, headstart.health, etc.)
  return 'production';
}

/**
 * Checks if running in local development environment
 * @returns true if on localhost/127.0.0.1
 */
export function isLocalDevelopment(): boolean {
  return getEnvironment() === 'local';
}

/**
 * Checks if running in dev environment
 * @returns true if on dev.app.headstart.health
 */
export function isDevEnvironment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Checks if running in production environment
 * @returns true if on production domains
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Gets the appropriate cookie domain for the current environment
 * @returns undefined for localhost, '.dev.app.headstart.health' for dev, '.headstart.health' for production
 */
export function getCookieDomain(): string | undefined {
  const env = getEnvironment();
  
  switch (env) {
    case 'local':
      // No domain restriction for localhost
      return undefined;
    case 'development':
      // Specific to dev subdomain
      return '.dev.app.headstart.health';
    case 'production':
      // Shared across all production subdomains
      return '.headstart.health';
    default:
      return '.headstart.health';
  }
}